"use client";

import { useState, useEffect } from "react";
import {
  Bot,
  ChevronDown,
  Check,
  CircleAlert,
  Wrench,
} from "lucide-react";
import { Markdown } from "./markdown";
import { summarizeInput, toolLabel } from "@/lib/agent/tool-display";

/**
 * 是否允许展开查看工具的原始 JSON 输出。
 * 仅本地调试（localhost / 127.0.0.1）开放；生产环境关闭，避免向终端用户暴露原始数据。
 * 在挂载后判定 hostname，规避 SSR 水合不一致。
 */
function useCanExpandRawOutput(): boolean {
  const [can, setCan] = useState(false);
  useEffect(() => {
    const h = window.location.hostname;
    setCan(h === "localhost" || h === "127.0.0.1" || h === "::1");
  }, []);
  return can;
}

/**
 * 将 agent.history（HistoricalEvent[]）渲染为对话流。
 * 事件类型：step | observation | error | retry | user_takeover。
 * step 含 reflection + action；done action 的 text 作为最终助手回复。
 */

// 与 @page-agent/core 的 HistoricalEvent 形状对齐（只取渲染所需字段）。
export interface HistoryEvent {
  type: "step" | "observation" | "error" | "retry" | "user_takeover";
  stepIndex?: number;
  reflection?: {
    evaluation_previous_goal?: string;
    memory?: string;
    next_goal?: string;
  };
  action?: {
    name: string;
    input?: Record<string, unknown> & { text?: string; success?: boolean };
    output?: string;
  };
  message?: string;
  attempt?: number;
  maxAttempts?: number;
}

function ToolCard({
  action,
  nextGoal,
}: {
  action: NonNullable<HistoryEvent["action"]>;
  nextGoal?: string;
}) {
  const [open, setOpen] = useState(false);
  const canExpandRaw = useCanExpandRawOutput();
  const isDone = action.name === "done";
  // 页面操作类工具（点击/滚动）用人话目标描述替代无意义的"元素 #11"。
  // next_goal 来自大模型对该步操作的陈述（如"点击导航栏中的'明星'链接[4],进入明星榜单页面。"），
  // 这里裁成短动作：去掉方括号索引注释、去掉句号后多余解释，并限制长度，避免撑乱卡片布局。
  // 没有可用 goal 则回退到 summarizeInput（仍是"元素 #11"）。
  const isPageOp = action.name === "click_element_by_index" || action.name === "scroll";
  const summary = (() => {
    if (!isPageOp) return summarizeInput(action.name, action.input);
    const raw = (nextGoal || "").trim();
    if (!raw) return summarizeInput(action.name, action.input);
    // 去掉 [数字] 形式的索引注释；以第一个句号/逗号后的说明部分截断，只保留动作本身。
    const noIndex = raw.replace(/\s*\[\d+\]\s*/g, " ").trim();
    const firstClause = noIndex.split(/[，。,；;]/)[0]?.trim() || noIndex;
    const cleaned = firstClause.replace(/^点击/, "点击").slice(0, 24);
    return cleaned || summarizeInput(action.name, action.input);
  })();
  // 生产环境（非 localhost）不允许展开原始 JSON 输出。
  const hasDetail = canExpandRaw && Boolean(action.output && action.output.length > 0);

  return (
    <div className="rounded-xl border border-sky-100 bg-white text-sm dark:border-sky-900/40 dark:bg-slate-900/40">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((v) => !v)}
        className={`flex w-full items-center gap-2 px-3 py-2 text-left ${hasDetail ? "cursor-pointer hover:bg-sky-50/60 dark:hover:bg-sky-950/20" : "cursor-default"}`}
        aria-expanded={open}
      >
        {isDone ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
        ) : (
          <Wrench className="h-3.5 w-3.5 shrink-0 text-sky-500" />
        )}
        <span className="font-medium text-slate-700 dark:text-slate-200">
          {toolLabel(action.name)}
        </span>
        {summary && (
          <span className="min-w-0 flex-1 truncate text-xs text-slate-400 dark:text-slate-500">{summary}</span>
        )}
        {hasDetail && (
          <ChevronDown
            className={`ml-auto h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {open && hasDetail && (
        <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words border-t border-sky-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-800 dark:text-slate-300">
          {action.output}
        </pre>
      )}
    </div>
  );
}

export interface Turn {
  userText: string;
  events: HistoryEvent[];
}

export function AgentMessageList({ turns }: { turns: Turn[] }) {
  return (
    <div className="space-y-4">
      {turns.map((turn, ti) => {
        const doneEvent = turn.events.find((e) => e.type === "step" && e.action?.name === "done");
        const finalText = doneEvent?.action?.input?.text || "";
        const steps = turn.events.filter((e) => e.type === "step" && e.action?.name !== "done");
        const errors = turn.events.filter((e) => e.type === "error");

        return (
          <div key={ti} className="space-y-3 animate-fade-in">
            {/* 用户消息 */}
            <div className="flex justify-end">
              <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-sky-500 px-3.5 py-2 text-sm text-white shadow-sm">
                {turn.userText}
              </div>
            </div>

            {/* 助手侧：工具步骤 */}
            {steps.length > 0 && (
              <div className="space-y-2">
                {steps.map((s, si) => (
                  <ToolCard key={si} action={s.action!} nextGoal={s.reflection?.next_goal} />
                ))}
              </div>
            )}

            {/* 错误 */}
            {errors.map((e, ei) => (
              <div
                key={`err-${ei}`}
                className="flex items-start gap-2 rounded-xl border border-rose-100 bg-rose-50/60 px-3 py-2 text-sm text-rose-600 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300"
              >
                <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{e.message || "任务执行出错"}</span>
              </div>
            ))}

            {/* 最终回复 */}
            {finalText && (
              <div className="flex gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-sky-400 to-sky-600 text-white">
                  <Bot className="h-3.5 w-3.5" />
                </div>
                <div className="max-w-[85%] rounded-2xl rounded-tl-sm border border-slate-100 bg-slate-50 px-3.5 py-2 text-sm leading-6 text-slate-700 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-200">
                  <Markdown>{finalText}</Markdown>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
